import { SERVER_BASE_URI } from '@pray/shared/config';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

/**
 * The analytics endpoint the phones report listening progress through.
 *
 * There is no progress API. iOS and Android send a `Listen` (or `Watch`) event to `/track`, and
 * the server turns it into the row behind Continue Listening: past 95% the item is completed,
 * otherwise started, and anything under ten seconds is ignored. It stores started-or-completed
 * per item plus the most recent episode per series, and never a position in seconds, so what
 * crosses devices is the right episode rather than the right second.
 *
 * **This is their contract, not ours.** `server_pray`'s `ContentProgressEventProcessingService`
 * reads exactly four properties off the event and ignores the rest, and each of the four has a
 * rule that silently drops the event when it is broken:
 *
 * | Property | Unit | Dropped when |
 * |---|---|---|
 * | `item_id` | | falsy |
 * | `item_type` | | not `content` or `daily` |
 * | `ending_percentage` | fraction, 0..1 | falsy, so a literal `0` never lands |
 * | `listen_duration` | seconds | under 10 |
 *
 * Nothing about a rejection reaches the client: that branch swallows its own errors and the
 * endpoint answers 200 regardless. A silent drop is the only failure mode, which is why the
 * shape is asserted here rather than trusted.
 */
const TRACK_PATH = '/0.13/track';

/** Below this the server ignores the event outright (`MIN_LISTEN_DURATION_SECONDS`). */
export const MIN_LISTEN_DURATION_SECONDS = 10;

/** Strictly above this the item is marked completed rather than started. */
export const COMPLETED_PERCENTAGE = 0.95;

/** The item types the progress path accepts. Everything else falls out of the pipeline. */
const TRACKABLE_ITEM_TYPES = ['content', 'daily'];

export type ListenEventProperties = {
  item_id: string;
  item_type: string;
  /** A fraction of the whole, not a percentage. Where the playhead is, not how far it travelled. */
  ending_percentage: number;
  /** Seconds played in this sitting. */
  listen_duration: number;
  [key: string]: unknown;
};

export type TrackEvent = {
  name: string;
  /** Epoch SECONDS, matching the phones. See `buildEvent`. */
  timestamp: number;
  properties: ListenEventProperties;
};

/**
 * Whether the server would do anything with this event.
 *
 * Applied before sending, so a listener who paused after four seconds costs no request at all.
 * The thresholds are duplicated from the server on purpose: they are the contract, and a copy
 * that can be read beside the payload is worth more than a request whose rejection is invisible.
 */
export function isReportableListen(properties: Partial<ListenEventProperties> | null | undefined) {
  if (!properties) return false;
  const { item_id: itemId, item_type: itemType, ending_percentage: endingPercentage } = properties;
  const listenDuration = properties.listen_duration;

  if (!itemId || !itemType || !TRACKABLE_ITEM_TYPES.includes(itemType)) return false;
  // Falsy rather than `< 0` to match the server, which drops a literal 0 the same way.
  if (!endingPercentage || !Number.isFinite(endingPercentage)) return false;
  if (!Number.isFinite(listenDuration as number)) return false;

  return (listenDuration as number) >= MIN_LISTEN_DURATION_SECONDS;
}

function buildEvent(name: string, properties: ListenEventProperties): { events: TrackEvent[] } {
  /*
   * An array even for one event: the phones always send `events`, and the route maps over it.
   *
   * The timestamp is in SECONDS, not milliseconds. iOS builds it as
   * `Int64(Date().timeIntervalSince1970)` in `PrayTracking.swift`, and the route forwards it to
   * the events stream as `event_timestamp`. Sending milliseconds here would not break progress,
   * which never reads the field, but it would put every web row in the data lake a thousand times
   * further into the future than the rows beside it.
   */
  return { events: [{ name, timestamp: Math.floor(Date.now() / 1000), properties }] };
}

/**
 * Sends the event and forgets it.
 *
 * Signed in means `isUserLoggedIn`, not "there is a user object". The site stores a full user
 * record for a guest too, with `is_logged_in: false`, so the presence of the record says nothing.
 * Reading it as a sign-in was the first version of this file, and it sent the event for every
 * anonymous reader on the site until a real browser run showed the guest record.
 *
 * To be clear about why the gate exists: the endpoint would NOT refuse a guest. The site device
 * authenticates every visitor, and the token middleware falls back to the Pray session cookie, so
 * a guest's event is accepted and written against their own row. The gate is what APP-50 asks
 * for, a product choice, not a technical necessity. Its cost is the reader who listens and then
 * signs in later in the same browser: that listening is not reported.
 *
 * Progress reporting is never worth an error in front of a listener, and the server does not
 * report a rejection anyway, so the promise is swallowed at the edge.
 */
export async function reportListenEvent(name: string, properties: ListenEventProperties) {
  if (!storage.isUserLoggedIn()) return false;
  if (!isReportableListen(properties)) return false;

  try {
    await api.executePostRequest(TRACK_PATH, buildEvent(name, properties));
    return true;
  } catch {
    return false;
  }
}

/**
 * The same event, sent from a document that is going away.
 *
 * An XHR started in `pagehide` is cancelled with the document, which is the one moment the
 * normal path cannot cover. `keepalive` outlives the page; `credentials` carries the Pray
 * session cookie, which `DecodeTokenMiddleware` accepts in place of the Authorization header,
 * so the request authenticates without the axios client's header set.
 */
export function reportListenEventOnPageLeave(name: string, properties: ListenEventProperties) {
  if (!storage.isUserLoggedIn()) return false;
  if (!isReportableListen(properties)) return false;
  if (typeof fetch !== 'function') return false;

  try {
    fetch(`${SERVER_BASE_URI}${TRACK_PATH}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'pray-platform': 'Web',
      },
      body: JSON.stringify(buildEvent(name, properties)),
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

export default {
  reportListenEvent,
  reportListenEventOnPageLeave,
  isReportableListen,
};
